import { config } from "../../../config/config";

export const PRESIGNED_RESOURCE_TYPES = ["users", "gyms", "software"] as const;
export type PresignedResourceType = (typeof PRESIGNED_RESOURCE_TYPES)[number];

export const PRIVATE_RESOURCE_TYPES = [
  "expenses",
  "payments",
  "machinery",
  "clients",
  "food",
  "merchandise",
  "suppliers",
  "trainers",
  "b2b-sales-invoices",
] as const;
export type PrivateResourceType = (typeof PRIVATE_RESOURCE_TYPES)[number];

export interface PresignedUrlBody {
  fileName: string;
  fileType: string;
  resourceType: PresignedResourceType;
  resourceId: string;
  /** Optional asset subpath, e.g. "logo", "banner". Key becomes resourceType/resourceId/assetPath/fileName */
  assetPath?: string;
}

export interface PresignedUrlResponse {
  url: string;
  key: string;
}

export const getPresignedUrlRequest = async (
  body: PresignedUrlBody,
  token: string,
): Promise<PresignedUrlResponse> => {
  const response = await fetch(`${config.API_URL}/aws/presigned-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let errorMessage = "Error al obtener la URL de subida";
    if (contentType?.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error ?? errorMessage;
      } catch {
        errorMessage = response.statusText ?? errorMessage;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export interface PrivatePresignedUrlBody {
  fileName: string;
  fileType: string;
  privateResourceType: PrivateResourceType;
  resourceId: string;
}

export const getPrivatePresignedUrlRequest = async (
  body: PrivatePresignedUrlBody,
  token: string,
): Promise<PresignedUrlResponse> => {
  const response = await fetch(`${config.API_URL}/aws/private-presigned-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let errorMessage = "Error al obtener la URL de subida privada";
    if (contentType?.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error ?? errorMessage;
      } catch {
        errorMessage = response.statusText ?? errorMessage;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const getPresignedDownloadUrlRequest = async (
  key: string,
  token: string,
): Promise<{ url: string }> => {
  const response = await fetch(`${config.API_URL}/aws/presigned-download`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ key }),
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let errorMessage = "Error al obtener la URL de descarga";
    if (contentType?.includes("application/json")) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error ?? errorMessage;
      } catch {
        errorMessage = response.statusText ?? errorMessage;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Deletes an object from S3 by key. Key must belong to the authenticated user's resource.
 * Fails silently (no throw) so upload flow is not blocked if delete fails.
 */
export const deleteS3ObjectRequest = async (
  key: string,
  token: string,
): Promise<void> => {
  try {
    const response = await fetch(`${config.API_URL}/aws/object`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ key }),
    });
    if (!response.ok) {
      console.warn("[S3] Delete object failed:", key, response.status);
    }
  } catch (err) {
    console.warn("[S3] Delete object error:", key, err);
  }
};
